import React, { useState, useMemo, useEffect, useCallback } from 'react';
import ReportLayout, {
  DateRangePicker,
} from './components/ReportLayout';
import {
  Receipt,
  FileText,
  Calculator,
  DollarSign,
  Building2,
  Percent,
  ChevronDown,
  AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import useGSTReport from '@/hooks/useGSTReport';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";

const fmt = (v) => {
  const n = Number(v);
  return isNaN(n) ? '0.00' : n.toLocaleString(undefined, { minimumFractionDigits: 2 });
};

const TABS = [
  { key: 'invoice', label: 'Invoice-wise' },
  { key: 'hsn', label: 'HSN Summary' },
  { key: 'b2cs', label: 'B2CS' },
];

// Column definitions per tab
const INVOICE_COLUMNS = [
  {
    key: 'billNumber', label: 'Bill No.', sortable: true,
    render: (v) => <span className="font-mono text-sm font-medium text-teal-700">{v}</span>,
  },
  {
  key: 'finalizedAt', label: 'Date', sortable: true,
  render: (v) => (
    <span className="text-gray-600">
      {v
        ? new Date(v).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
        : '-'}
    </span>
  ),
},
  {
    key: 'branchName', label: 'Branch', sortable: true,
    render: (v) => <span className="text-gray-600 text-xs">{v || '-'}</span>,
  },
  {
    key: 'taxableAmount', label: 'Taxable Amt', sortable: true, align: 'right',
    render: (v) => <span className="text-gray-900">{fmt(v)}</span>,
  },
  {
    key: 'cgstRate', label: 'CGST Rate', sortable: true, align: 'right',
    render: (v) => <span className="text-gray-600">{fmt(v)}%</span>,
  },
  {
    key: 'cgstAmount', label: 'CGST', sortable: true, align: 'right',
    render: (v) => <span className="text-gray-900">{fmt(v)}</span>,
  },
  {
    key: 'sgstRate', label: 'SGST Rate', sortable: true, align: 'right',
    render: (v) => <span className="text-gray-600">{fmt(v)}%</span>,
  },
  {
    key: 'sgstAmount', label: 'SGST', sortable: true, align: 'right',
    render: (v) => <span className="text-gray-900">{fmt(v)}</span>,
  },
  {
    key: 'totalTax', label: 'Total Tax', sortable: true, align: 'right',
    render: (v) => <span className="font-semibold text-gray-900">{fmt(v)}</span>,
  },
  {
    key: 'invoiceValue', label: 'Bill Value', sortable: true, align: 'right',
    render: (v) => <span className="font-semibold text-teal-700">{fmt(v)}</span>,
  },
];

const HSN_COLUMNS = [
  { key: 'hsn', label: 'HSN', sortable: true, render: (v) => <span className="font-mono text-sm font-medium">{v}</span> },
  { key: 'description', label: 'Description', sortable: true },
  { key: 'uqc', label: 'UQC', sortable: false },
  { key: 'totalQuantity', label: 'Qty', sortable: true, align: 'right', render: (v) => <span>{Number(v)}</span> },
  { key: 'totalValue', label: 'Total Value', sortable: true, align: 'right', render: (v) => <span>{fmt(v)}</span> },
  { key: 'rate', label: 'Rate %', sortable: true, align: 'right', render: (v) => <span>{fmt(v)}%</span> },
  { key: 'taxableValue', label: 'Taxable Value', sortable: true, align: 'right', render: (v) => <span>{fmt(v)}</span> },
  { key: 'cgstAmount', label: 'CGST', sortable: true, align: 'right', render: (v) => <span>{fmt(v)}</span> },
  { key: 'sgstAmount', label: 'SGST', sortable: true, align: 'right', render: (v) => <span>{fmt(v)}</span> },
  { key: 'totalTax', label: 'Total Tax', sortable: true, align: 'right', render: (v) => <span className="font-semibold">{fmt(v)}</span> },
];

const B2CS_COLUMNS = [
  { key: 'type', label: 'Type', sortable: true },
  { key: 'placeOfSupply', label: 'Place of Supply', sortable: true },
  { key: 'rate', label: 'Rate %', sortable: true, align: 'right', render: (v) => <span>{fmt(v)}%</span> },
  { key: 'taxableValue', label: 'Taxable Value', sortable: true, align: 'right', render: (v) => <span>{fmt(v)}</span> },
  { key: 'cgstAmount', label: 'CGST', sortable: true, align: 'right', render: (v) => <span>{fmt(v)}</span> },
  { key: 'sgstAmount', label: 'SGST', sortable: true, align: 'right', render: (v) => <span>{fmt(v)}</span> },
  { key: 'totalTax', label: 'Total Tax', sortable: true, align: 'right', render: (v) => <span className="font-semibold">{fmt(v)}</span> },
];

const COLUMN_MAP = {
  invoice: INVOICE_COLUMNS,
  hsn: HSN_COLUMNS,
  b2cs: B2CS_COLUMNS,
};

const getSearchFields = (viewBy) => {
  if (viewBy === 'hsn') return ['hsn', 'description', 'rate', 'taxableValue'];
  if (viewBy === 'b2cs') return ['type', 'placeOfSupply', 'rate', 'taxableValue'];
  return ['billNumber', 'finalizedAt', 'branchName', 'taxableAmount', 'cgstAmount', 'sgstAmount', 'totalTax', 'invoiceValue'];
};

export default function GSTReport() {
  const { hasPermission } = useAuth();

  if (!hasPermission(P.REPORTS.FINANCIAL.SCREEN)) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 font-semibold">
          You do not have permission to access GST Report
        </p>
      </div>
    );
  }
  // Default to previous month (GSTR-1 is filed monthly for the completed month)
  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

  // Format as a LOCAL yyyy-mm-dd. toISOString() converts to UTC first, which in IST
  // (UTC+5:30) turns local midnight into the previous day — "Today" would have asked
  // for yesterday.
  const fmtDate = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const [startDate, setStartDate] = useState(fmtDate(prevMonth));
  const [endDate, setEndDate] = useState(fmtDate(prevMonthEnd));
  const [selectedPreset, setSelectedPreset] = useState('lastMonth');
  const [viewBy, setViewBy] = useState('invoice');
  const [selectedBranchId, setSelectedBranchId] = useState(''); // '' = All Branches

  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, summary, loading, downloadLoading, error, fetchGSTReport, downloadGSTReport } = useGSTReport();

  // Apply button handler (uses current state directly)
  const doFetch = useCallback(() => {
    if (startDate && endDate) {
      fetchGSTReport({ fromDate: startDate, toDate: endDate, viewBy });
      setCurrentPage(1);
      setSortColumn('');
      setSearchQuery('');
    }
  }, [startDate, endDate, viewBy, fetchGSTReport]);

  // Auto-fetch on mount and when viewBy tab changes
  useEffect(() => {
    if (startDate && endDate) {
      fetchGSTReport({ fromDate: startDate, toDate: endDate, viewBy });
      setCurrentPage(1);
      setSortColumn('');
      setSearchQuery('');
    }
  }, [viewBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = COLUMN_MAP[viewBy] || INVOICE_COLUMNS;

  // ── Extract branches from summary array ──────────────────────
  const branches = useMemo(() => {
    if (!summary || !Array.isArray(summary)) return [];
    return summary
      .filter((b) => b.branchId && b.branchName)
      .map((b) => ({ id: b.branchId, name: b.branchName }));
  }, [summary]);

  const hasMultipleBranches = branches.length > 1;

  // Branch the GSTR-1 download should be scoped to. A branch user only ever gets their
  // own branch back from the API, so the selector is not rendered for them and
  // selectedBranchId stays empty — fall back to the single branch in the response so
  // they can still download. An owner seeing several branches must pick one, since a
  // GSTR-1 covering multiple branches would be filed as one branch's return.
  const downloadBranchId =
    selectedBranchId || (branches.length === 1 ? branches[0].id : '');

  // ── Per-branch summary data (the raw array) ──────────────────
  const branchSummaries = useMemo(() => {
    if (!summary) return [];
    return Array.isArray(summary) ? summary : [summary];
  }, [summary]);

  // Aggregate summary — sum across all branches (or single branch if selected)
  const aggregatedSummary = useMemo(() => {
    if (!summary) return null;
    const items = Array.isArray(summary) ? summary : [summary];
    if (items.length === 0) return null;
    const filtered = selectedBranchId ? items.filter((b) => b.branchId === selectedBranchId) : items;
    if (filtered.length === 0) return null;
    return {
      totalInvoices: filtered.reduce((s, b) => s + (b.totalInvoices ?? 0), 0),
      totalTaxable: filtered.reduce((s, b) => s + (b.totalTaxable ?? 0), 0),
      totalCgst: filtered.reduce((s, b) => s + (b.totalCgst ?? 0), 0),
      totalSgst: filtered.reduce((s, b) => s + (b.totalSgst ?? 0), 0),
      totalTax: filtered.reduce((s, b) => s + (b.totalTax ?? 0), 0),
      totalValue: filtered.reduce((s, b) => s + (b.totalValue ?? 0), 0),
    };
  }, [summary, selectedBranchId]);

  // Summary cards from API
  const summaryCards = useMemo(() => {
    if (!aggregatedSummary) return [];
    return [
      { title: 'Total Invoices', value: String(aggregatedSummary.totalInvoices ?? 0), subtitle: 'Tax invoices generated', icon: FileText },
      { title: 'Taxable Amount', value: `₹${fmt(aggregatedSummary.totalTaxable)}`, subtitle: 'Before tax', icon: Calculator },
      { title: 'Total CGST', value: `₹${fmt(aggregatedSummary.totalCgst)}`, subtitle: 'Central GST', icon: Percent },
      { title: 'Total SGST', value: `₹${fmt(aggregatedSummary.totalSgst)}`, subtitle: 'State GST', icon: Percent },
      { title: 'Total Tax', value: `₹${fmt(aggregatedSummary.totalTax)}`, subtitle: 'CGST + SGST', icon: Receipt },
      { title: 'Invoice Value', value: `₹${fmt(aggregatedSummary.totalValue)}`, subtitle: 'Total with tax', icon: DollarSign },
    ];
  }, [aggregatedSummary]);

  // ── Filter table data by selected branch ─────────────────────
  const branchFilteredData = useMemo(() => {
    if (!data) return [];
    if (!selectedBranchId) return data;
    return data.filter((row) => row.branchId === selectedBranchId);
  }, [data, selectedBranchId]);

  // Sorting
  const sortedData = useMemo(() => {
    if (!branchFilteredData || !sortColumn) return branchFilteredData;
    const sorted = [...branchFilteredData];
    sorted.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (sortColumn === 'finalizedAt') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      return sortDirection === 'asc'
        ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
        : String(bVal ?? '').localeCompare(String(aVal ?? ''));
    });
    return sorted;
  }, [branchFilteredData, sortColumn, sortDirection]);

  // Search filter
  const filteredData = useMemo(() => {
    if (!searchQuery.trim() || !sortedData) return sortedData;
    const query = searchQuery.toLowerCase().trim();
    const fields = getSearchFields(viewBy);
    return sortedData.filter(row =>
      fields.some(field => {
        const val = row[field];
        if (val === null || val === undefined) return false;
        return String(val).toLowerCase().includes(query);
      })
    );
  }, [sortedData, searchQuery, viewBy]);

  // Compute intra-state breakdown from table data (invoice view)
  const taxBreakdown = useMemo(() => {
    if (!branchFilteredData || branchFilteredData.length === 0) return [];
    if (viewBy === 'invoice') {
      const byRate = {};
      branchFilteredData.forEach(row => {
        const rate = Number(row.cgstRate || 0) + Number(row.sgstRate || 0);
        const key = rate.toFixed(2);
        if (!byRate[key]) byRate[key] = { rate, cgst: 0, sgst: 0, total: 0 };
        byRate[key].cgst += Number(row.cgstAmount || 0);
        byRate[key].sgst += Number(row.sgstAmount || 0);
        byRate[key].total += Number(row.totalTax || 0);
      });
      return Object.values(byRate).sort((a, b) => a.rate - b.rate);
    }
    if (viewBy === 'hsn' || viewBy === 'b2cs') {
      const byRate = {};
      branchFilteredData.forEach(row => {
        const rate = Number(row.rate || 0);
        const key = rate.toFixed(2);
        if (!byRate[key]) byRate[key] = { rate, cgst: 0, sgst: 0, total: 0 };
        byRate[key].cgst += Number(row.cgstAmount || 0);
        byRate[key].sgst += Number(row.sgstAmount || 0);
        byRate[key].total += Number(row.totalTax || 0);
      });
      return Object.values(byRate).sort((a, b) => a.rate - b.rate);
    }
    return [];
  }, [branchFilteredData, viewBy]);

  const totalCgst = taxBreakdown.reduce((s, r) => s + r.cgst, 0);
  const totalSgst = taxBreakdown.reduce((s, r) => s + r.sgst, 0);
  const grandTotal = totalCgst + totalSgst;

  // Handlers
  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleGenerateReport = () => {
    if (!downloadBranchId) {
      toast.error('Please select a specific branch to download GSTR-1 report');
      return;
    }
    downloadGSTReport({ fromDate: startDate, toDate: endDate, viewBy, branchId: downloadBranchId });
  };

  const handleRefresh = () => {
    doFetch();
  };

  /**
   * Resolve a preset to a concrete date range.
   *
   * Other reports send `period` to the backend and let SQL resolve it, but the GST
   * endpoint only accepts fromDate/toDate, so the range is computed here. Week starts
   * Monday to match the backend's DATE_TRUNC('week', ...) used by every other report.
   */
  const rangeForPreset = (preset) => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const d = today.getDate();

    switch (preset) {
      case 'today':
        return [new Date(y, m, d), new Date(y, m, d)];
      case 'yesterday': {
        const yest = new Date(y, m, d - 1);
        return [yest, yest];
      }
      case 'thisWeek': {
        // getDay(): 0 = Sunday. Shift so Monday is the first day.
        const daysSinceMonday = (today.getDay() + 6) % 7;
        return [new Date(y, m, d - daysSinceMonday), new Date(y, m, d)];
      }
      case 'thisMonth':
        return [new Date(y, m, 1), new Date(y, m, d)];
      case 'lastMonth':
        // Day 0 of this month = last day of the previous month.
        return [new Date(y, m - 1, 1), new Date(y, m, 0)];
      default:
        return null;
    }
  };

  const handlePresetSelect = (preset) => {
  setSelectedPreset(preset);

  const range = rangeForPreset(preset);
  if (!range) return;

  const [from, to] = range;
  const fromStr = fmtDate(from);
  const toStr = fmtDate(to);

  setStartDate(fromStr);
  setEndDate(toStr);

  // Fetch right away with the computed dates instead of waiting for Apply
  fetchGSTReport({ fromDate: fromStr, toDate: toStr, viewBy });
  setCurrentPage(1);
  setSortColumn('');
  setSearchQuery('');
};

  const handleBranchChange = (branchId) => {
    setSelectedBranchId(branchId);
    setCurrentPage(1);
  };

  // Pagination
  const paginatedData = useMemo(() => {
    if (!filteredData) return [];
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  const pagination = {
    currentPage,
    totalPages: Math.ceil((filteredData?.length || 0) / pageSize),
    totalItems: filteredData?.length || 0,
    pageSize,
    onPageChange: setCurrentPage,
    onPageSizeChange: (size) => {
      setPageSize(size);
      setCurrentPage(1);
    },
  };

  // Selected branch name for download button
  const selectedBranchName = branches.find((b) => b.id === selectedBranchId)?.name;

  // Filters
  const filters = (
    <div className="space-y-4">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        // Editing a date by hand means the range no longer matches the highlighted
        // preset, so drop the highlight rather than leave it showing a stale range.
        onStartDateChange={(d) => { setStartDate(d); setSelectedPreset(null); }}
        onEndDateChange={(d) => { setEndDate(d); setSelectedPreset(null); }}
        onPresetSelect={handlePresetSelect}
        selectedPreset={selectedPreset}
      />

      <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-gray-200">
        {/* View By Toggle */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">View By</label>
          <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setViewBy(tab.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewBy === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Branch Filter */}
        {hasMultipleBranches && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Branch</label>
            <div className="relative">
              <Building2 size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={selectedBranchId}
                onChange={(e) => handleBranchChange(e.target.value)}
                className="pl-8 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
              >
                <option value="">All Branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-transparent">Apply</label>
          <button
            onClick={doFetch}
            disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Apply'}
          </button>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-transparent">Download</label>
          <div className="flex items-center gap-2">
            <button
              onClick={handleGenerateReport}
              disabled={downloadLoading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 ${
                selectedBranchId
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
              }`}
            >
              <FileText size={16} />
              {downloadLoading ? 'Preparing...' : 'Download GSTR-1'}
            </button>
            {!selectedBranchId && hasMultipleBranches && (
              <span className="flex items-center gap-1 text-xs text-amber-600">
                <AlertCircle size={12} />
                Select a branch first
              </span>
            )}
            {selectedBranchId && (
              <span className="text-xs text-gray-500 italic whitespace-nowrap">{selectedBranchName}</span>
            )}
            {!hasMultipleBranches && (
              <span className="text-xs text-gray-500 italic whitespace-nowrap">(Ready to upload on GST Portal)</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <ReportLayout
      title="GST Report"
      description="View tax details and download portal-ready GSTR-1 returns"
      icon={Receipt}
      exportPermission={P.REPORTS.FINANCIAL.GST_DOWNLOAD}
      color="emerald"
      category="Financial"
      categoryId="financial"
      summaryCards={summaryCards}
      columns={columns}
      data={paginatedData}
      filters={filters}
      loading={loading}
      error={error}
      pagination={pagination}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={handleSort}
      onSearch={handleSearch}
      searchPlaceholder={
        viewBy === 'hsn' ? 'Search by HSN, description...' :
        viewBy === 'b2cs' ? 'Search by type, place of supply...' :
        'Search by bill number, branch, amount...'
      }
      onRefresh={handleRefresh}
    >
      {/* Branch-wise Bifurcation */}
      {hasMultipleBranches && !selectedBranchId && branchSummaries.length > 1 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
            <h3 className="font-semibold text-gray-900">Branch-wise Bifurcation</h3>
            <p className="text-xs text-gray-500">Tax summary for each branch</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Branch</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Invoices</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Taxable Amount</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">CGST</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">SGST</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Tax</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Invoice Value</th>
                  <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {branchSummaries.map((branch) => (
                  <tr key={branch.branchId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 size={14} className="text-gray-400" />
                        <span className="font-medium text-gray-900">{branch.branchName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700 font-medium">{branch.totalInvoices ?? 0}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700">₹{fmt(branch.totalTaxable)}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700">₹{fmt(branch.totalCgst)}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700">₹{fmt(branch.totalSgst)}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900 font-semibold">₹{fmt(branch.totalTax)}</td>
                    <td className="px-4 py-3 text-right text-sm text-teal-700 font-semibold">₹{fmt(branch.totalValue)}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleBranchChange(branch.branchId)}
                        className="px-2.5 py-1 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-md transition-colors border border-teal-200"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {/* Totals Row */}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-3 text-sm text-gray-900">
                    Total ({branchSummaries.length} branches)
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {branchSummaries.reduce((s, b) => s + (b.totalInvoices ?? 0), 0)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    ₹{fmt(branchSummaries.reduce((s, b) => s + (b.totalTaxable ?? 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    ₹{fmt(branchSummaries.reduce((s, b) => s + (b.totalCgst ?? 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    ₹{fmt(branchSummaries.reduce((s, b) => s + (b.totalSgst ?? 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    ₹{fmt(branchSummaries.reduce((s, b) => s + (b.totalTax ?? 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-teal-700">
                    ₹{fmt(branchSummaries.reduce((s, b) => s + (b.totalValue ?? 0), 0))}
                  </td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Selected Branch Indicator */}
      {selectedBranchId && hasMultipleBranches && (
        <div className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded-lg px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-teal-600" />
            <span className="text-sm font-medium text-teal-800">
              Showing data for: <span className="font-semibold">{selectedBranchName}</span>
            </span>
          </div>
          <button
            onClick={() => handleBranchChange('')}
            className="text-xs font-medium text-teal-700 hover:text-teal-900 underline transition-colors"
          >
            Show all branches
          </button>
        </div>
      )}

      {/* Tax Summary by Rate */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-200">
          <h3 className="font-semibold text-gray-900">Tax Summary by Rate</h3>
          <p className="text-xs text-gray-500">
            Breakdown for GSTR-1 filing
            {selectedBranchId && selectedBranchName && (
              <span className="ml-1">— {selectedBranchName}</span>
            )}
          </p>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Intra-State Summary */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Building2 size={16} className="text-gray-500" />
                <span className="font-medium text-gray-700">Intra-State (CGST + SGST)</span>
              </div>
              <div className="space-y-2">
                {taxBreakdown.map((entry) => (
                  <div key={entry.rate} className="flex justify-between text-sm">
                    <span className="text-gray-600">@{fmt(entry.rate)}% ({fmt(entry.rate / 2)}% + {fmt(entry.rate / 2)}%)</span>
                    <span className="font-medium">₹{fmt(entry.total)}</span>
                  </div>
                ))}
                {taxBreakdown.length === 0 && (
                  <div className="text-sm text-gray-400">No data</div>
                )}
                <div className="flex justify-between text-sm font-semibold pt-2 border-t border-gray-200">
                  <span className="text-gray-700">Total</span>
                  <span className="text-emerald-600">₹{fmt(grandTotal)}</span>
                </div>
              </div>
            </div>

            {/* Grand Total */}
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-2 mb-3">
                <Receipt size={16} className="text-emerald-600" />
                <span className="font-medium text-gray-700">Total Tax Liability</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">CGST</span>
                  <span className="font-medium">₹{fmt(totalCgst)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">SGST</span>
                  <span className="font-medium">₹{fmt(totalSgst)}</span>
                </div>
                <div className="flex justify-between text-sm font-bold pt-2 border-t border-emerald-300">
                  <span className="text-gray-900">Grand Total</span>
                  <span className="text-emerald-700">₹{fmt(grandTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ReportLayout>
  );
}
